// src/pages/ModoApostaPage.js
import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import estruturaCategorias from "../services/estruturaCategorias";
import { categoriasPorModo } from "../services/estruturaCategorias";
import CardCategoria from "../components/CardCategoria";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";
import "./ModoApostaPage.css"; 
import AlertaUrgente from "../components/AlertaUrgente";

function ModoApostaPage() {
  const { modo } = useParams();
  const [premiacao, tipoAposta] = modo.split("-");
  const [uid, setUid] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [usuarioFirestore, setUsuarioFirestore] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [totalApostado, setTotalApostado] = useState(0);
  const [categoriasAtualizadas, setCategoriasAtualizadas] = useState([]);
  const [carregandoAtualizacao, setCarregandoAtualizacao] = useState(true);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [categoriasDinamicas, setCategoriasDinamicas] = useState([]);
  const [previsoesAtivas, setPrevisoesAtivas] = useState({});
  const totalCategorias = Object.keys(estruturaCategorias[modo] || {}).length;
  const navigate = useNavigate();
  const imagemPerfilDefault = "https://firebasestorage.googleapis.com/v0/b/previsoesoscar.firebasestorage.app/o/fotosUsuarios%2Fdefaults%2Fperfil.png?alt=media&token=e1de994b-b337-41ea-992f-fb0929611d36";
  const imagemCabecalhoDefault = "https://firebasestorage.googleapis.com/v0/b/previsoesoscar.firebasestorage.app/o/fotosUsuarios%2Fdefaults%2Fcapa.png?alt=media&token=5a95f438-714b-4eea-a506-bdb619c43c5a"; 

  const textoTipoAposta =
    tipoAposta === "vencedores"
      ? "APOSTAS DOS VENCEDORES"
      : "APOSTAS DOS INDICADOS";

  const nomePremiacao = premiacao
    .replace(/oscar/, "OSCAR")
    .replace(/bafta/, "BAFTA")
    .replace(/sagawards/, "THE ACTOR AWARDS")
    .replace(/dga/, "DGA AWARDS")
    .replace(/pga/, "PGA AWARDS")
    .replace(/globodeouro/, "GLOBO DE OURO")
    .replace(/criticschoice/, "CRITICS CHOICE")
    .toUpperCase();

  // -------- utils de MIGRAÇÃO (para não perder dados existentes) --------
  function migrateLocal(oldKey, newKey) {
    try {
      const val = localStorage.getItem(oldKey);
      if (val && !localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, val);
      }
      if (val) localStorage.removeItem(oldKey);
    } catch {}
  }
  function migrateSession(oldKey, newKey) {
    try {
      const val = sessionStorage.getItem(oldKey);
      if (val && !sessionStorage.getItem(newKey)) {
        sessionStorage.setItem(newKey, val);
      }
      if (val) sessionStorage.removeItem(oldKey);
    } catch {}
  }

  // -------- auth + cache do usuarioFirestore (sem mexer no restante) --------
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUsuario(user);
        setUid(user.uid); // ✅ garante uid cedo

        const dadosSalvos = sessionStorage.getItem("usuarioFirestore");
        if (dadosSalvos) {
          setUsuarioFirestore(JSON.parse(dadosSalvos));
        } else {
          const docRef = doc(db, "usuarios", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const dados = docSnap.data();
            setUsuarioFirestore(dados);
            sessionStorage.setItem("usuarioFirestore", JSON.stringify(dados));
          } else {
            console.warn("Documento do usuário não encontrado no Firestore.");
          }
        }
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // -------- bloco AZUL: categorias atualizadas + última atualização (localStorage) --------
  useEffect(() => {
    if (!uid) {
      // sem uid ainda → não tenta ler; evita herdar dados de outro user
      setCategoriasAtualizadas([]);
      setUltimaAtualizacao(null);
      setCarregandoAtualizacao(false);
      return;
    }

    setCarregandoAtualizacao(true);

    // chaves NOVAS (com uid)
    const chaveCategoriasNew = `categoriasAtualizadas-${uid}-${modo}`;
    const chaveUltimaHoraNew = `ultimaAtualizacao-${uid}-${modo}`;

    // migração das chaves ANTIGAS (sem uid), se existirem
    const chaveCategoriasOld = `categoriasAtualizadas-${modo}`;
    const chaveUltimaHoraOld = `ultimaAtualizacao-${modo}`;
    migrateLocal(chaveCategoriasOld, chaveCategoriasNew);
    migrateLocal(chaveUltimaHoraOld, chaveUltimaHoraNew);

    const armazenado = localStorage.getItem(chaveCategoriasNew);
    const hora = localStorage.getItem(chaveUltimaHoraNew);

    let categoriasValidas = [];
    const agora = new Date();

    if (armazenado) {
      try {
        const dados = JSON.parse(armazenado);
        const categorias = dados.categorias || {};
        for (const [categoria, validadeStr] of Object.entries(categorias)) {
          const validade = new Date(validadeStr);
          if (validade > agora) {
            categoriasValidas.push(categoria);
          }
        }
      } catch (e) {
        console.error("Erro ao ler categorias atualizadas:", e);
      }
    }

    setCategoriasAtualizadas(categoriasValidas);
    setUltimaAtualizacao(hora ? new Date(hora) : null);
    setCarregandoAtualizacao(false);
  }, [modo, uid]);

  function handleClickCategoria(cat) {
    if (cat.status !== "aberto") return;
    navigate(`/perfil/${modo}/${cat.id}`);
  }

  // -------- leitura principal do SNAP + cache de categorias dinâmicas (sessionStorage) --------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      setUid(user.uid);
      if (!estruturaCategorias[modo]) return;

      try {
        // 🔹 Snapshot com apostas por modo
        const refSnap = doc(db, "usuarios", user.uid, "snapDropdown2026", "dados");
        const snap = await getDoc(refSnap);
        const dadosSnap = snap.exists() ? snap.data() : {};

        const dadosModo = dadosSnap[modo] || {};
        let total = 0;
        const novasPrevisoes = {};
        const categoriasComAposta = {};

        for (const [categoriaId, posicoesObj] of Object.entries(dadosModo)) {
          const posicoes = Object.values(posicoesObj || {})
            .filter((p) => p && typeof p === "object" && p.titulo && p.titulo.trim() !== "")
            .sort((a, b) => a.ordem - b.ordem);

          if (posicoes.length > 0) {
            novasPrevisoes[categoriaId] = posicoes;
            categoriasComAposta[categoriaId] = true;
            total++;
          }
        }

        setPrevisoesAtivas(novasPrevisoes);
        setTotalApostado(total);
        setCarregando(false);

        // 🔹 Cache dinâmico das categorias (sessionStorage) — agora namespaced por uid
        const cacheKeyNew = `status-categorias-${uid}-${modo}`;
        const cacheKeyOld = `status-categorias-${modo}`;
        migrateSession(cacheKeyOld, cacheKeyNew);

        const cacheCategorias = sessionStorage.getItem(cacheKeyNew);
        if (cacheCategorias) {
          const categoriasTemp = JSON.parse(cacheCategorias);
          setCategoriasDinamicas(
            categoriasTemp.map((cat) => ({
              ...cat,
              temAposta: !!categoriasComAposta[cat.id],
            }))
          );
          return;
        }

        // Sem cache → lê no Firestore
        const refSubcolecao = collection(db, "modos", modo, "categorias");
        const snapshotCategorias = await getDocs(refSubcolecao);

        const listaIds = Object.keys(estruturaCategorias[modo]);
        const mapaCategorias = {};
        snapshotCategorias.forEach((docSnap) => {
          mapaCategorias[docSnap.id] = docSnap.data();
        });

        const categoriasTemp = [];
        for (const id of listaIds) {
          const dados = mapaCategorias[id];
          if (!dados) continue;

          const nome = nomesBonitosCategoria[id] || id;
          const status = dados.aberto ? "aberto" : "fechado";
          const mensagem = status === "aberto" ? "Aberto" : (dados.mensagem?.trim() || "");

          categoriasTemp.push({
            id,
            nome,
            status,
            mensagem,
            temAposta: !!categoriasComAposta[id],
          });
        }

        setCategoriasDinamicas(categoriasTemp);
        sessionStorage.setItem(cacheKeyNew, JSON.stringify(categoriasTemp));
      } catch (erro) {
        console.error("Erro ao buscar dados do modo:", erro);
      }
    });

    return () => unsubscribe();
  }, [modo, uid]);

  return (
    <div className="categoria-aposta-container">
      <AlertaUrgente />
      <div className="header">
        {/* IMAGEM DE FUNDO */}
        <div className="foto-cabecalho">
          <img
            className="imagem-capa"
            src={usuarioFirestore?.fotoCabecalho || imagemCabecalhoDefault}
            alt="Foto de cabeçalho"
          />
        </div>
        <div className="header-buttons">
          <button className="btn black" onClick={() => navigate(`/perfil`)}>
            VOLTAR PARA O PERFIL
          </button>
        </div>
        {/* FOTO DE PERFIL SOBREPOSTA */}
        <div className={`foto-usuario ${usuarioFirestore?.especialista ? "especialista" : ""}`}>
          <img
            className="foto-perfil"
            src={usuarioFirestore?.fotoPerfil || imagemPerfilDefault}
            alt="Foto de perfil"
          />
        </div>
      </div>

      <div className="usuario-info">
        <h1>
          {usuarioFirestore?.nome && usuarioFirestore?.sobrenome
            ? `${usuarioFirestore.nome} ${usuarioFirestore.sobrenome}`
            : "Usuário"}
        </h1>
        {usuarioFirestore?.especialista && (
          <div className="tag-especialista">ESPECIALISTA DO OSCAR</div>
        )}
        <hr className="dotted" />
        <p className="sub-apostas">{textoTipoAposta}</p>
        <h2>{nomePremiacao}</h2>
      </div>

      <div className="Containerspace">
        <div className="titulo-categorias2">APOSTAS NAS CATEGORIAS</div>

        <div className="status-blocos">
          <div className="bloco-status amarelo">
            <img src="https://i.imgur.com/t0gsZRT.png" alt="Estrela" />
            <span>
              {carregando
                ? "Carregando..."
                : `Você apostou em ${totalApostado} de ${totalCategorias} categorias do ${nomePremiacao}`}
            </span>
          </div>

          {!carregandoAtualizacao && categoriasAtualizadas.length > 0 && (
            <div className="bloco-status azul">
              <img src="https://i.imgur.com/qO8UixZ.png" alt="Atualizado" />
              <div>
                <span>
                  Você atualizou recentemente <br />
                  {categoriasAtualizadas.length} de {totalCategorias} categorias do {nomePremiacao}
                </span>
                {ultimaAtualizacao instanceof Date && !isNaN(ultimaAtualizacao) && (
                  <p className="ultima-atualizacao2">
                    Última atualização:{" "}
                    {ultimaAtualizacao.toLocaleDateString("pt-PT", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric"
                    })}
                    , às{" "}
                    {ultimaAtualizacao.toLocaleTimeString("pt-PT", {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="cards-container">
          {categoriasDinamicas.map((cat) => (
            <CardCategoria
              key={cat.id}
              nome={cat.nome}
              status={cat.status}
              mensagem={cat.mensagem}
              temAposta={cat.temAposta}
              onClick={() => handleClickCategoria(cat)}
            />
          ))}
        </div>

        <div className="cabecalho-minhas-previsoes">MINHAS PREVISÕES</div>
        <div className="blocos-previsoes">
          {(categoriasPorModo[modo] || []).map((categoriaId) => {
            const posicoes = previsoesAtivas[categoriaId];
            if (!posicoes || !Array.isArray(posicoes) || posicoes.length === 0) return null;

            const nomeCategoria = nomesBonitosCategoria[categoriaId] || categoriaId;

            return (
              <div className="bloco-previsao" key={categoriaId}>
                <div className="cabecalho-previsao amarelo">{nomeCategoria.toUpperCase()}</div>
                <div className="lista-posicoes">
                  {posicoes.map((pos, index) => (
                    <div className="item-posicao" key={index}>
                      <div className={`posicao-circulo cor-${index + 1}`}>{index + 1}º</div>
                      <div className="ligacao-horizontal" />
                      <div className="wrapper-circular">
                        <img
                          className="poster-candidato"
                          src={pos.imagem || "https://i.imgur.com/6MbQoCt.png"}
                          alt={pos.titulo}
                        />
                      </div>
                      <div className="info-candidato">
                        <strong>{pos.titulo}</strong>
                        {pos.filme && <div className="subinfo">{pos.filme}</div>}
                        {pos.pais && <div className="subinfo">{pos.pais}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="btn-atualizar"
                  onClick={() => navigate(`/perfil/${modo}/${categoriaId}`)}
                >
                  ATUALIZAR
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ModoApostaPage;
